import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/AppLayout";
import StationCard from "../components/StationCard";
import api from "../services/api";

function PlaylistRow({ pl, onDeleted }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      setLoading(true);
      try {
        const res = await api.get(`/api/playlists/${pl.id}`);
        setDetail(res.data);
      } catch { /* ignore */ } finally { setLoading(false); }
    }
  };

  const removeItem = async (item) => {
    try {
      await api.delete(`/api/playlists/${pl.id}/items/${item.id}`);
      setDetail((d) => ({ ...d, items: d.items.filter((x) => x.id !== item.id) }));
    } catch { /* ignore */ }
  };

  const deletePlaylist = async () => {
    if (!window.confirm(t("playlists.confirmDelete", { name: pl.name }))) return;
    try {
      await api.delete(`/api/playlists/${pl.id}`);
      onDeleted(pl.id);
    } catch { /* ignore */ }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      <div className="flex items-center gap-3 p-4">
        <button onClick={toggle} className="flex-1 text-left">
          <div className="font-semibold text-gray-900">{pl.name}</div>
          <div className="text-xs text-gray-500">
            {t("playlists.stationCount", { count: pl.items_count })}{pl.is_public ? ` • ${t("playlists.public")}` : ""}
          </div>
        </button>
        <button onClick={toggle} className="text-sm text-purple-600">{open ? t("playlists.hide") : t("playlists.open")}</button>
        <button onClick={deletePlaylist} className="w-9 h-9 rounded-full bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 flex items-center justify-center">✕</button>
      </div>
      {open && (
        <div className="px-4 pb-4">
          {loading ? (
            <p className="text-gray-500 text-sm">{t("common.loading")}</p>
          ) : detail && detail.items.length > 0 ? (
            <div className="grid gap-2">
              {detail.items.map((it) => (
                <StationCard key={it.id} station={it} allowFavorite={false} allowPlaylist={false} onRemove={removeItem} />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{t("playlists.emptyItems")}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Playlists() {
  const { t } = useTranslation();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/playlists");
      setPlaylists(res.data);
    } catch {
      setPlaylists([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/api/playlists", { name: name.trim() });
      setName("");
      load();
    } catch { /* ignore */ }
  };

  return (
    <AppLayout title={t("playlists.title")}>
      <form onSubmit={create} className="flex gap-2 mb-6">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("playlists.newPlaceholder")}
          className="flex-1 rounded-xl border border-gray-200 px-4 py-3"
        />
        <button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white px-5 rounded-xl">{t("playlists.create")}</button>
      </form>

      {loading ? (
        <p className="text-gray-500">{t("common.loading")}</p>
      ) : playlists.length === 0 ? (
        <p className="text-gray-500">{t("playlists.empty")}</p>
      ) : (
        <div className="grid gap-3">
          {playlists.map((pl) => (
            <PlaylistRow key={pl.id} pl={pl} onDeleted={(id) => setPlaylists((prev) => prev.filter((p) => p.id !== id))} />
          ))}
        </div>
      )}
    </AppLayout>
  );
}
